import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import clsx from 'clsx';

import { Breadcrumb, Button, IconButton, Label, LABEL_TYPE, Tag } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { OfflineBlockedPopup } from '@/components/modals/OfflineBlockedPopup';
import { queryClient } from '@/configuration/react-query';
import { AuthContext, EventContext } from '@/contexts';
import { PERMISSION_TYPE, TABS } from '@/enums';
import { getEventStatusLabel } from '@/helpers/EventsHelper';
import { showToast, ToastType } from '@/helpers/toast';
import { EVENT_CURRENT_TAB_STORAGE_KEY } from '@/pages/Event/eventTabStorage';
import { EVENTS_REFETCH_INTERVAL } from '@/pages/Events';
import { Events as Calendar, EditPenIcon, TrashIcon } from '@/resources';
import { usePublishEventMutation } from '@/services';
import { getTestId, TestIds } from '@/shared/test-ids';

type HeaderProps = {
  currentTab: string;
  setIsDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
  isOffline?: boolean;
  offlineEventName?: string;
};

export const Header = ({
  currentTab,
  setIsDeleteModalOpen,
  setCurrentTab,
  isOffline = false,
  offlineEventName = ''
}: HeaderProps) => {
  const navigate = useNavigate();
  const [t] = useTranslation('common');
  const { eventData, refetch } = useContext(EventContext);
  const { permissions } = useContext(AuthContext);
  const [showOfflinePopup, setShowOfflinePopup] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  const OFFLINE_ALLOWED_TABS = [TABS.MUSIC, TABS.DETAILS];
  // Mirrors the [Authorize(nameof(PermissionValue.UpdateEvent))] the publish endpoint enforces.
  const hasUpdateEventPermission = permissions.includes(PERMISSION_TYPE.UPDATE_EVENT);

  const { mutate: publishEvent, isLoading: isPublishing } = usePublishEventMutation(queryClient, {
    onSuccess: () => {
      setIsPublishModalOpen(false);
      showToast(ToastType.SUCCESS, t('ceremonyPublished'));
    },
    onError: (error) => {
      setIsPublishModalOpen(false);
      showToast(ToastType.ERROR, error.response.data.ErrorMessages[0].DisplayMessage);
    }
  });

  // A ceremony with no device has nothing to publish to - the API returns it unchanged.
  const canPublish = hasUpdateEventPermission && !isOffline;
  const publishButton = (
    <Button
      className='w-full md:w-32'
      variant={ButtonVariant.PRIMARY}
      disabled={!eventData?.device?.id || isPublishing}
      dataTestId={TestIds.events.publishButton}
      handleOnClick={() => setIsPublishModalOpen(true)}>
      {t('publish')}
    </Button>
  );

  useEffect(() => {
    const interval = setInterval(() => {
      !document.hidden && refetch();
    }, EVENTS_REFETCH_INTERVAL);

    return () => clearInterval(interval);
  }, [refetch]);

  return (
    <div className='w-full'>
      <div className='!md:w-full md:mx-20 md:mt-7'>
        <Breadcrumb
          icon={<Calendar className='stroke-[0.5] text-lg text-primary-blue' />}
          iconPath={'/events'}
          path={[
            {
              label: eventData?.name ?? offlineEventName,
              url: eventData?.id ? `/events/${eventData.id}` : '/events'
            },
            { label: t(currentTab) }
          ]}
        />
        {!isOffline && (
          <div className='mt-8 hidden flex-row items-center justify-between md:flex'>
            <div className='flex flex-row items-center justify-center gap-x-6'>
              <Label text={eventData?.name} type={LABEL_TYPE.H1} />
              <Tag
                value={getEventStatusLabel(eventData?.status)}
                statusDescription={eventData?.statusDescription}
              />
            </div>
            <div className='flex flex-row items-center gap-4'>
              <IconButton
                variant={IconButtonVariant.PRIMARY}
                handleOnClick={() => navigate(`/events/${eventData?.id}/edit`)}>
                <EditPenIcon className='h-4 w-3 text-primary-blue md:h-5 md:w-4' />
              </IconButton>
              <IconButton
                variant={IconButtonVariant.PRIMARY}
                handleOnClick={() => setIsDeleteModalOpen(true)}>
                <TrashIcon className='h-4 w-3 md:h-5 md:w-4' />
              </IconButton>
              {canPublish && publishButton}
            </div>
          </div>
        )}
        {isOffline && (
          <div className='mt-8 hidden md:flex'>
            <Label text={offlineEventName} type={LABEL_TYPE.H1} />
          </div>
        )}
        {/* The title row above is desktop-only, so mobile gets its own Publish row rather than
            losing the action entirely. */}
        {canPublish && <div className='mt-4 md:hidden'>{publishButton}</div>}
        <div
          className={
            'no-scrollbar mt-4 flex flex-row overflow-x-scroll md:mt-8 lg:overflow-x-hidden'
          }>
          <OfflineBlockedPopup
            isOpen={showOfflinePopup}
            onClose={() => setShowOfflinePopup(false)}
          />
          {Object.values(TABS).map((tab) => {
            const isTabDisabledOffline = isOffline && !OFFLINE_ALLOWED_TABS.includes(tab);
            return (
              <div
                key={tab}
                data-testid={getTestId(TestIds.events.tab, tab)}
                className={clsx(
                  'cursor-pointer whitespace-nowrap rounded px-5 py-2.5',
                  currentTab === tab && 'bg-secondary-pale-blue-gray font-bold',
                  isTabDisabledOffline && 'cursor-not-allowed opacity-30'
                )}
                onClick={() => {
                  if (isTabDisabledOffline) {
                    setShowOfflinePopup(true);
                    return;
                  }
                  localStorage.setItem(EVENT_CURRENT_TAB_STORAGE_KEY, tab);
                  setCurrentTab(tab);
                }}>
                {t(tab)}
              </div>
            );
          })}
        </div>
        <div className='w-full border-b' />
      </div>
      {isPublishModalOpen && (
        <ConfirmationModal
          isOpen={isPublishModalOpen}
          isDisabled={isPublishing}
          onClose={() => setIsPublishModalOpen(false)}
          onConfirm={() => publishEvent(Number(eventData?.id))}
          message={t('publishCeremonyMessage')}
        />
      )}
    </div>
  );
};
